import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Loader2, AlertTriangle } from "lucide-react";

const API = getApiUrl();

const SSO_PROVIDERS = [
  { key: "google_sso", name: "Google SSO", description: "Sign in with Google — used by agents and shoppers site-wide." },
  { key: "facebook_sso", name: "Facebook SSO", description: "Sign in with Facebook — available to shoppers on the public site." },
];

export default function SsoIntegrationSection() {
  const [toggles, setToggles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/integrations/visibility`, { withCredentials: true })
      .then((r) => setToggles(r.data?.toggles || {}))
      .catch(() => toast.error("Failed to load SSO visibility settings"))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (key, checked) => {
    setSaving(key);
    setToggles((prev) => ({ ...prev, [key]: checked }));
    try {
      await axios.put(`${API}/admin/integrations/visibility/${key}`, { enabled: checked }, { withCredentials: true });
      toast.success(`${SSO_PROVIDERS.find((p) => p.key === key)?.name} ${checked ? "enabled" : "disabled"}`);
    } catch (e) {
      setToggles((prev) => ({ ...prev, [key]: !checked }));
      toast.error("Failed to update — please try again");
    } finally {
      setSaving(null);
    }
  };

  const googleOff = toggles.google_sso === false;
  const facebookOff = toggles.facebook_sso === false;

  return (
    <div className="space-y-4" data-testid="sso-integration-section">
      <p className="text-sm text-muted-foreground">
        When an SSO provider is switched off here, its sign-in button is hidden
        entirely across the site (agent login and shopper sign-in) rather than
        shown disabled.
      </p>
      {!loading && googleOff && (
        <div
          className="border-2 border-amber-400 bg-amber-50 p-3 rounded flex gap-2 items-start text-sm"
          data-testid="sso-lockout-warning"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-amber-900">
            <p className="font-medium">Agents cannot sign in right now</p>
            <p className="text-amber-800 text-xs leading-relaxed mt-0.5">
              Google is the agent portal's only sign-in method — with it off, the
              agent login page shows "Sign-in is currently unavailable" and no
              agent can get in.
              {facebookOff && " Shoppers on the public site are locked out too, since both providers are disabled."}
            </p>
          </div>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        SSO_PROVIDERS.map(({ key, name, description }) => (
          <Card key={key} data-testid={`sso-card-${key}`}>
            <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
              <div>
                <CardTitle className="text-base">{name}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
              <Switch
                checked={toggles[key] !== false}
                disabled={saving === key}
                onCheckedChange={(checked) => handleToggle(key, checked)}
                data-testid={`sso-visibility-switch-${key}`}
              />
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs text-muted-foreground">
                {toggles[key] !== false ? "Visible and usable." : "Disabled — the sign-in button is hidden site-wide."}
              </p>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
